import { useQuery } from '@tanstack/vue-query'
import { apiClient } from '@/apis/instances/api-client'
import { checkApiResponse } from '@/apis/instances/check-api-response'
import { ApiV2ReservationgroupElFindParams } from '@/apis/swaggers/v2/v2-swagger'
import { QUERY_KEY } from '@/apis/swaggers/swagger-docs'
import { MaybeGetter, QueryComposableParams, QueryOptions } from '@/types/query-options'
import { resolveMaybeGetter } from '@/util/resolve-maybe-getter'

type QueryParams = QueryComposableParams<ApiV2ReservationgroupElFindParams, unknown>

const fetchReservationGroupList = async (params: ApiV2ReservationgroupElFindParams) => {
  const { data } = await apiClient.v2.apiV2ReservationgroupElFind(params);
  return checkApiResponse(data);
}

export const v2ReservationGroupKeys = {
  all: [QUERY_KEY.v2apiV2ReservationgroupElFind] as const,
  list: (params: ApiV2ReservationgroupElFindParams) => [...v2ReservationGroupKeys.all, params] as const,
}

/** 명령형(await) 호출용 쿼리 정의 — `queryClient.fetchQuery(v2ReservationGroupFindQueryOptions(params))` */
export const v2ReservationGroupFindQueryOptions = (params: ApiV2ReservationgroupElFindParams) => ({
  queryKey: v2ReservationGroupKeys.list(params),
  queryFn: () => fetchReservationGroupList(params),
})

export const useV2ReservationGroupFindQuery = ({ params, options }: QueryParams) => {
  return useQuery(() => ({
    queryKey: v2ReservationGroupKeys.list(resolveMaybeGetter(params)),
    queryFn: () => fetchReservationGroupList(resolveMaybeGetter(params)),
    ...resolveMaybeGetter(options ?? {}),
  }))
}

/**
 * 그룹 목록 표준 조회 — mutation·FCM invalidation 이 완전 커버하므로 staleTime Infinity.
 * 특수 조건 조회(기간 필터·keyword 등)는 기존 v2ReservationGroupFindQueryOptions 를 그대로 사용한다 (staleTime 0 유지).
 */
export const v2ReservationGroupListQueryOptions = (hospitalID: string) => ({
  // swagger 생성 타입이 hospitalID 를 boolean 으로 잘못 문서화하고 있어 캐스팅 (서버 스웨거 주석 오류)
  ...v2ReservationGroupFindQueryOptions({ hospitalID, isRemoved: false, key: 'sortNum', way: 1 } as unknown as ApiV2ReservationgroupElFindParams),
  staleTime: Infinity,
  gcTime: Infinity,
})

/**
 * 표준 목록 조회의 선언형 구독 훅 — 표준 파라미터·staleTime 은 v2ReservationGroupListQueryOptions 이 관리하고,
 * enabled 등 컴포넌트별 옵션은 사용처(options)에서 제어한다.
 */
export const useV2ReservationGroupListQuery = ({ hospitalID, options }: {
  hospitalID: MaybeGetter<string | undefined>
  options?: QueryOptions
}) => {
  return useQuery(() => {
    const id = resolveMaybeGetter(hospitalID)
    return {
      ...v2ReservationGroupListQueryOptions(id as string),
      enabled: !!id,
      ...resolveMaybeGetter(options ?? {}),
    }
  })
}
